<template>
  <div class="modal fade show purchase-return-detail-modal" aria-labelledby="purchaseReturnDetail">
    <div class="modal-dialog modal-dialog-centered modal-custom">
      <div class="modal-content">
        <div class="row">
          <div class="col-12">
            <div class="d-flex justify-content-between align-items-center px-1 my-1">
              <h1 class="mb-0">Purchase Return Detail</h1>
              <button @click="closeModal" type="button" class="btn-close" aria-label="Close"></button>
            </div>
          </div>
        </div>

        <hr class="mt-0">

        <div class="modal-body pb-5">
          <div v-if="loading" class="text-center py-5">Loading...</div>

          <div v-if="! loading && bill">
            <div class="row gy-1">
              <div class="col-12 col-md-3" v-if="partyName">
                <span class="highlight-row">Party Name: </span>
                <span>{{ partyName }}</span>
              </div>
              <div class="col-12 col-md-3" v-if="bill.bill_number">
                <span class="highlight-row">Bill Number: </span>
                <span>{{ bill.bill_number }}</span>
              </div>
              <div class="col-12 col-md-3" v-if="bill.date">
                <span class="highlight-row">Date: </span>
                <span>{{ bill.date }}</span>
              </div>
              <div class="col-12 col-md-3" v-if="bill.status">
                <span class="highlight-row">Status: </span>
                <span>{{ bill.status }}</span>
              </div>
              <div class="col-12 col-md-3" v-if="businessName">
                <span class="highlight-row">Business: </span>
                <span>{{ businessName }}</span>
              </div>
              <div class="col-12 col-md-3" v-if="accountHeadName">
                <span class="highlight-row">Account Head: </span>
                <span>{{ accountHeadName }}</span>
              </div>
              <div class="col-12 col-md-3" v-if="bill.warehouse_name">
                <span class="highlight-row">Warehouse: </span>
                <span>{{ bill.warehouse_name }}</span>
              </div>
              <div class="col-12" v-if="bill.description">
                <span class="highlight-row">Memo: </span>
                <span>{{ bill.description }}</span>
              </div>
            </div>

            <div class="col-12 mt-8">
              <div class="table-responsive">
                <table class="table custom-table-padding">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th class="text-right">Quantity</th>
                      <th class="text-right">Rate</th>
                      <th class="text-right">Amount</th>
                      <th class="text-right">Discount</th>
                      <th class="text-right">VAT</th>
                      <th class="text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(general, i) in generalList" :key="i">
                      <td>{{ productName(general) }}</td>
                      <td class="text-right">{{ general.quantity }}</td>
                      <td class="text-right">{{ commaFormat(general.rate) }}</td>
                      <td class="text-right">{{ commaFormat(general.amount) }}</td>
                      <td class="text-right">{{ commaFormat(general.discount_amount) }}</td>
                      <td class="text-right">{{ commaFormat(general.vat_amount) }}</td>
                      <td class="text-right">{{ commaFormat(general.total_amount) }}</td>
                    </tr>
                  </tbody>
                </table>
                <p v-if="! generalList.length" class="text-center">No items</p>
              </div>
            </div>

            <div class="col-12 mt-1">
              <div class="summary-card">
                <table class="table">
                  <tbody>
                    <tr>
                      <td>Subtotal</td>
                      <td class="currency">{{ commaFormat(bill.subtotal_amount) }}</td>
                    </tr>
                    <tr>
                      <td>VAT Amount</td>
                      <td class="currency">{{ commaFormat(bill.vat_amount) }}</td>
                    </tr>
                    <tr class="highlight-row border-top">
                      <td>Total Amount</td>
                      <td class="currency">{{ commaFormat(bill.total_amount) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-12 text-center pb-3">
            <button class="btn btn-secondary me-1" @click="closeModal">Close</button>
            <button
              class="btn btn-success"
              :disabled="approving || loading || ! bill"
              @click="approve"
            >
              Approve
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, inject } from 'vue'
import { useRoute } from 'vue-router'
import handlePurchaseAndSalesReturn from '@/services/modules/purchaseSalesReturn'
import figureFormatter from '@/services/utils/figureFormatter'

const props = defineProps({
  purchaseReturnId: {
    type: [Number, String],
    required: true
  },
  approving: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['onApprove', 'onClose'])

const route = useRoute()
const showError = inject('showError')
const { fetchPurchaseReturnBill } = handlePurchaseAndSalesReturn()
const { commaFormat } = figureFormatter()

const loading = ref(false)
const bill = ref(null)

const generalList = computed(() => {
  if (! bill.value) return []
  if (! bill.value.general) return []
  return bill.value.general
})

const partyName = computed(() => {
  if (! bill.value) return ''
  if (! bill.value.contact) return ''
  return bill.value.contact.name
})

const businessName = computed(() => {
  if (! bill.value) return ''
  if (! bill.value.business) return ''
  return bill.value.business.name
})

const accountHeadName = computed(() => {
  if (! bill.value) return ''
  if (! bill.value.account_head) return ''
  return bill.value.account_head.name
})

const productName = (general) => {
  if (! general.product) return ''
  return general.product.name
}

const closeModal = () => {
  emit('onClose')
}

const approve = () => {
  if (props.approving) return
  emit('onApprove', props.purchaseReturnId)
}

const getBill = () => {
  loading.value = true
  const query = `?company_id=${route.params.companyId}`
  fetchPurchaseReturnBill(props.purchaseReturnId, query)
    .then(res => {
      if (! res.status) return
      bill.value = res.data
    })
    .catch(() => showError('Failed to load purchase return detail'))
    .finally(() => (loading.value = false))
}

onMounted(() => {
  getBill()
})
</script>

<style scoped>
.purchase-return-detail-modal {
  display: block;
  background: #1c1d1c33;
}
.modal-custom {
  max-width: 75vw;
}
.highlight-row {
  font-weight: bold;
}
.currency {
  text-align: right;
}
.mt-8 {
  margin-top: 8px;
}
.summary-card td {
  padding: 5px 0;
}
</style>
